/**
 * DAFNE Time Period Configuration
 *
 * Defines meal times, analysis periods, and post-meal windows.
 * These can be customized per user but these are the defaults.
 *
 * Last reviewed: February 2026
 */

// =============================================================================
// MEAL/ENTRY PERIOD DEFINITIONS
// =============================================================================

/**
 * Time windows for each DAFNE diary marker.
 * These define the "home" time range for each marker.
 * The actual logged time determines placement in Other columns.
 */
export const PERIOD_HOURS = {
  /** 3am check window (overnight fasting) - 2:00-4:59 */
  "3AM": { start: 2, end: 5 },

  /** Breakfast window (BF) - 5:00-10:59 */
  B: { start: 5, end: 11 },

  /** Lunch window (L) - 11:00-14:59 */
  L: { start: 11, end: 15 },

  /** Dinner window (D) - 15:00-20:59 */
  EM: { start: 15, end: 21 },

  /** Before Bed window (BB) - 21:00-1:59 */
  BT: { start: 21, end: 2 },
} as const;

// =============================================================================
// DEFAULT MEAL TIMES (HH:MM format)
// =============================================================================

export const DEFAULT_MEAL_TIMES = {
  B: "07:30",
  L: "12:30",
  EM: "18:30",
  BT: "22:00",
  "3AM": "03:00",
} as const;

// =============================================================================
// PATTERN ANALYSIS TIME PERIODS
// =============================================================================

export const ANALYSIS_TIME_PERIODS = {
  dawn: { start: 5, end: 8, label: "Dawn (5-8 AM)" },
  morning: { start: 8, end: 12, label: "Morning (8 AM-12 PM)" },
  afternoon: { start: 12, end: 17, label: "Afternoon (12-5 PM)" },
  evening: { start: 17, end: 21, label: "Evening (5-9 PM)" },
  night: { start: 21, end: 24, label: "Night (9 PM-12 AM)" },
  overnight: { start: 0, end: 5, label: "Overnight (12-5 AM)" },
} as const;

// =============================================================================
// POST-MEAL ANALYSIS WINDOWS
// =============================================================================

export const POST_MEAL_WINDOWS = {
  /** Hours after meal to look for BG peak */
  B: { peakStart: 1, peakEnd: 3, label: "Post-Breakfast" },
  L: { peakStart: 1, peakEnd: 3, label: "Post-Lunch" },
  EM: { peakStart: 1, peakEnd: 3, label: "Post-Evening Meal" },
} as const;

// =============================================================================
// PERIOD SUGGESTION FUNCTION
// =============================================================================

/**
 * Suggest a period tag (marker) based on hour of day.
 * Returns the marker whose time window contains this hour.
 *
 * @param hour - Hour in 24-hour format (0-23)
 * @returns Suggested period marker
 */
export function suggestPeriodFromHour(
  hour: number,
): "B" | "L" | "EM" | "BT" | "3AM" {
  // 3AM window: 2:00-4:59
  if (hour >= 2 && hour < 5) return "3AM";
  // Breakfast window: 5:00-10:59
  if (hour >= 5 && hour < 11) return "B";
  // Lunch window: 11:00-14:59
  if (hour >= 11 && hour < 15) return "L";
  // Dinner window: 15:00-20:59
  if (hour >= 15 && hour < 21) return "EM";
  // Before Bed window: 21:00-1:59 (spans midnight)
  return "BT";
}
